import { useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { LEGAL_PAGES, getLegalPage } from "@/data/legal";
import { getLegalBody } from "@/data/legal_content";

export default function Legal() {
  const { slug } = useParams();
  const page = getLegalPage(slug);

  const parsedLines = useMemo(() => {
    if (!page) return [];
    const body = getLegalBody(slug, page.title);
    return body.split("\n").map((line, i) => {
      const key = `${slug}-line-${i}`;
      if (line.startsWith("**") && line.endsWith("**")) return { key, type: "h2", text: line.replaceAll("**", "") };
      if (line.startsWith("- ")) return { key, type: "li", text: line.replace("- ", "") };
      return { key, type: "p", text: line };
    });
  }, [slug, page]);

  const otherPages = useMemo(() => LEGAL_PAGES.filter((p) => p.slug !== slug), [slug]);

  if (!page) return <Navigate to="/" replace />;

  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid={`legal-page-${slug}`}>
      <div className="eyebrow mb-4">Legal · Policy</div>
      <h1 className="font-display font-bold text-4xl lg:text-5xl tracking-tighter">{page.title}</h1>
      <p className="mt-4 text-muted-foreground max-w-2xl">{page.intro}</p>
      <div className="gold-line max-w-[80px] my-8" />

      <article className="prose prose-neutral dark:prose-invert max-w-none whitespace-pre-line leading-relaxed text-[15px]" style={{ whiteSpace: "pre-line" }}>
        {parsedLines.map((l) => {
          if (l.type === "h2") return <h2 key={l.key} className="font-display font-bold text-xl mt-8 mb-3">{l.text}</h2>;
          if (l.type === "li") return <li key={l.key} className="ml-6 text-muted-foreground">{l.text}</li>;
          return <p key={l.key} className="text-muted-foreground">{l.text}</p>;
        })}
      </article>

      <div className="mt-16 pt-8 border-t border-border">
        <div className="eyebrow mb-4">Other Policies</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
          {otherPages.map((p) => (
            <Link key={p.slug} to={`/legal/${p.slug}`} className="text-sm text-muted-foreground hover:text-gold-500 transition-colors">{p.title}</Link>
          ))}
        </div>
      </div>
    </div>
  );
}
